import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Users, ArrowRight } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { apiUrl } from '../config/api'

export const JoinPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const [session, setSession] = useState<any>(null)
  const [isJoining, setIsJoining] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (sessionId) {
      loadSessionAndAutoJoin()
    }
  }, [sessionId])

  const loadSessionAndAutoJoin = async () => {
    try {
      // Load session details
      const response = await fetch(apiUrl(`/api/sessions/${sessionId}`))
      if (!response.ok) {
        throw new Error('Session not found')
      }
      const sessionData = await response.json()
      setSession(sessionData)
      
      // Check session status
      if (sessionData.status === 'terminated') {
        toast.error('This session has been terminated')
        navigate('/')
        return
      }
      
      if (sessionData.status === 'active') {
        toast.error('This session is already active and no longer accepting new participants')
        navigate('/')
        return
      }
      
      // Auto-join the guest using the pre-configured guest language
      await autoJoinSession(sessionData.guestLanguage)
      
    } catch (error) {
      console.error('Error loading session:', error)
      toast.error('Session not found or has expired')
      navigate('/')
    } finally {
      setIsLoading(false)
    }
  }

  const autoJoinSession = async (guestLanguage: string) => {
    setIsJoining(true)
    
    try {
      const response = await fetch(apiUrl(`/api/sessions/${sessionId}/join`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          guestLanguage
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to join session')
      }

      await response.json()
      
      // Mark this user as a guest in session storage
      sessionStorage.setItem(`guest_${sessionId}`, 'true')
      
      // Navigate directly to the session
      navigate(`/session/${sessionId}`)
    } catch (error) {
      console.error('Error joining session:', error)
      // Only show error toast if we're still on this page (not navigating)
      setTimeout(() => {
        if (window.location.pathname.includes('/join/')) {
          toast.error('Failed to join session. Please try again.')
        }
      }, 100)
      setIsJoining(false)
    }
  }

  if (isLoading || isJoining) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full text-center">
          <div className="flex justify-center mb-6">
            <div className="flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full">
              <Users className="w-8 h-8 text-primary-600" />
            </div>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {isLoading ? 'Loading Session...' : 'Joining Session...'}
          </h2>
          
          <div className="flex justify-center mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
          
          <p className="text-gray-600">
            {isLoading ? 'Please wait while we load the session details' : 'You\'re being connected to the translation session'}
          </p>
          
          {session && (
            <div className="mt-6 p-4 bg-white rounded-lg shadow-sm border">
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-700">
                <span className="font-medium">{session.ownerLanguage}</span>
                <ArrowRight className="w-4 h-4 text-gray-400" />
                <span className="font-medium">{session.guestLanguage}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  // Error state - should rarely be reached due to auto-navigation
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full text-center">
        <div className="flex justify-center mb-6">
          <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full">
            <Users className="w-8 h-8 text-red-600" />
          </div>
        </div>
        
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Unable to Join Session
        </h2>
        
        <p className="text-gray-600 mb-6">
          There was an issue joining the translation session. Please try again or contact the session owner.
        </p>
        
        <button
          onClick={() => navigate('/')}
          className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
        >
          Go Home
        </button>
      </div>
    </div>
  )
}